import { gql, useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import DefectCodesChart from "./DefectCodesChart";
import DefectCodeImages from "./DefectCodeImages";
import ReportList from "./ReportList";

const FETCH_FACTORY_QC_ANALYTICS = gql`
  query FETCH_FACTORY_QC_ANALYTICS(
    $id: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    factoryQcAnalytics(id: $id, startDate: $start, endDate: $end)
  }
`;

const FactoryDetail = ({ id, start, end }) => {
  const { loading, error, data } = useQuery(FETCH_FACTORY_QC_ANALYTICS, {
    variables: { id, start, end },
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const res = JSON.parse(data.factoryQcAnalytics);

  return (
    <div>
      <div className="space-y-12">
        {res.products.map((product, index) => (
          <FactoryProduct
            key={product.id}
            product={product}
            factoryId={id}
            start={start}
            end={end}
            className={index > 0 ? "border-t-4 pt-12" : ""}
          />
        ))}
      </div>
    </div>
  );
};

const FactoryProduct = ({ product, factoryId, start, end, className }) => {
  const [selectedDefectCodeId, setSelectedDefectCodeId] = useState(null);
  const defectCodes = Object.values(product.defectCodes).sort(
    (a, b) => b.qty - a.qty,
  );
  const selectedDefectCode = defectCodes.find(
    (defectCode) => defectCode.id === selectedDefectCodeId,
  );

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <h2>{product.name}</h2>
      <ProductChart
        product={product}
        selectedDefectCodeId={selectedDefectCodeId}
      />
      <div className="grid gap-8 xl:grid-cols-2">
        <div>
          <DefectCodesChart
            data={defectCodes.map((defectCode) => ({
              id: defectCode.id,
              name: defectCode.description,
              value: defectCode.qty,
            }))}
            selectedId={selectedDefectCodeId}
            onSelect={setSelectedDefectCodeId}
            horizontal
          />
        </div>
        {selectedDefectCode && (
          <div>
            <DefectCodeImages
              key={selectedDefectCode.id}
              factoryId={factoryId}
              productId={product.id}
              defectCode={selectedDefectCode}
              start={start}
              end={end}
            />
          </div>
        )}
      </div>
      <ReportList reports={product.origin_reports} />
    </div>
  );
};

const ProductChart = ({ product, selectedDefectCodeId }) => {
  const [showChecked, setShowChecked] = useState(false);

  const selectedDefectCode = Object.values(product.defectCodes).find(
    (defectCode) => defectCode.id === selectedDefectCodeId,
  );
  const monthlySelectedIssueQty = product.origin_reports.reduce(
    (quantities, report) => {
      if (selectedDefectCodeId === null) return quantities;

      const month = report.created_at.slice(0, 7);
      quantities[month] =
        (quantities[month] || 0) +
        (report.issue_type_quantities?.[selectedDefectCodeId] || 0);
      return quantities;
    },
    {},
  );
  const chartData = product.reports.map((report) => {
    const issueQty =
      selectedDefectCodeId === null
        ? report.issue_qty
        : monthlySelectedIssueQty[report.month] || 0;

    return {
      month: report.month,
      checkQty: report.check_qty,
      issueQty,
      checkedWithoutIssues: report.check_qty - issueQty,
      issueRate: report.check_qty
        ? Math.round((issueQty * 10000) / report.check_qty) / 100
        : 0,
    };
  });
  const checkQty = chartData.reduce((sum, report) => sum + report.checkQty, 0);
  const issueQty = chartData.reduce((sum, report) => sum + report.issueQty, 0);
  const issueRate = checkQty
    ? Math.round((issueQty * 10000) / checkQty) / 100
    : 0;

  return (
    <div>
      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span aria-hidden className="h-2 w-2 rounded-full bg-amber-500" />
          <strong>{issueRate}%</strong> Issue
        </span>
        <button
          type="button"
          onClick={() => setShowChecked(!showChecked)}
          aria-pressed={showChecked}
          title={showChecked ? "Hide checked" : "Show checked"}
          className={`flex items-center gap-1 ${showChecked ? "" : "opacity-40"}`}
        >
          <span aria-hidden className="h-2 w-2 rounded-full bg-gray-300" />
          <strong>{checkQty}</strong> Checked
        </button>
        <span className="flex items-center gap-1">
          <span aria-hidden className="h-2 w-2 rounded-full bg-red-400" />
          <strong>{issueQty}</strong>{" "}
          {selectedDefectCode ? selectedDefectCode.description : "Issues"}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart
          data={chartData}
          margin={{ top: 24, right: 8, bottom: 0, left: 0 }}
        >
          <XAxis dataKey="month" />
          <YAxis yAxisId="qty" allowDecimals={false} />
          <YAxis yAxisId="rate" orientation="right" unit="%" />
          <Tooltip content={CustomTooltip} />
          <Bar
            yAxisId="qty"
            dataKey="issueQty"
            stackId="checked"
            maxBarSize={20}
            name="Issues"
            fill="#f87171"
            label={{
              position: "top",
              fill: "#f87171",
              formatter: (value) => value || "",
            }}
          />
          {showChecked && (
            <Bar
              yAxisId="qty"
              dataKey="checkedWithoutIssues"
              stackId="checked"
              maxBarSize={20}
              name="Checked"
              fill="#d1d5db"
            />
          )}
          <Line
            yAxisId="rate"
            type="monotone"
            dataKey="issueRate"
            name="Issue %"
            stroke="#f59e0b"
            label={{
              position: "top",
              fill: "#f59e0b",
              formatter: (value) => (value ? `${value}%` : ""),
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div className="rounded bg-white p-2 dark:bg-gray-800">
        <div>{label}</div>
        <div>Checked: {data.checkQty}</div>
        <div>Issues: {data.issueQty}</div>
        <div>Issue {data.issueRate}%</div>
      </div>
    );
  }

  return null;
};

export default FactoryDetail;
